import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { useAssets } from '../../../context/AssetsContext';

interface UseThreeSceneProps {
	mountRef: React.RefObject<HTMLDivElement>;
	boundingSize?: number;
	transparent?: boolean;
	objectHeight?: number;
	autoRotate?: boolean;
	enableControls?: boolean;
	minDistance?: number;
	maxDistance?: number;
	scale?: number;
}

export function useScene({
	mountRef,
	boundingSize = 800,
	transparent = false,
	objectHeight = 0,
	autoRotate = false,
	enableControls = true,
	minDistance,
	maxDistance,
	scale = 1,
}: UseThreeSceneProps) {
	const sceneRef = useRef<THREE.Scene | null>(null);
	const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
	const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
	const animationIdRef = useRef<number | null>(null);
	const controlsRef = useRef<OrbitControls | null>(null);
	const assets = useAssets();

	useEffect(() => {
		if (!mountRef.current) return;

		const renderer = new THREE.WebGLRenderer({
			antialias: true,
			alpha: transparent,
			precision: 'lowp',
			powerPreference: assets.lowPower ? 'low-power' : 'high-performance',
		});
		renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
		renderer.setPixelRatio(1);
		renderer.toneMapping = THREE.ACESFilmicToneMapping;
		renderer.toneMappingExposure = 1;
		renderer.shadowMap.enabled = true;
		renderer.shadowMap.type = THREE.PCFSoftShadowMap;

		if (assets.lowPower) {
			console.log('Low power device detected, setting renderer to low power mode');
			renderer.setPixelRatio(window.devicePixelRatio * 0.9);
		}

		renderer.setClearColor(
			transparent ? 0x000000 : 0xffffff,
			transparent ? 0 : 1
		);
		mountRef.current.appendChild(renderer.domElement);
		rendererRef.current = renderer;

		const scene = new THREE.Scene();
		if (!transparent) {
			scene.background = new THREE.Color(0xffffff);
		}
		sceneRef.current = scene;
		sceneRef.current.scale.set(scale, scale, scale);

		const camera = new THREE.PerspectiveCamera(
			10,
			mountRef.current.clientWidth / mountRef.current.clientHeight,
			15,
			15000
		);
		cameraRef.current = camera;

		const controls = new OrbitControls(camera, renderer.domElement);
		controls.enablePan = false;
		controls.enableZoom = enableControls;
		controls.enableRotate = enableControls;
		controls.dampingFactor = 0.2;
		controls.enableDamping = true;
		controls.autoRotate = autoRotate;
		if (minDistance) controls.minDistance = minDistance;
		if (maxDistance) controls.maxDistance = maxDistance;
		controls.minPolarAngle = 0;
		controls.maxPolarAngle = Math.PI / 2;
		controlsRef.current = controls;

		// // 1) Ambient (fill) light: gentle overall brightness
		const ambient = new THREE.AmbientLight(0xffd177, 0.3);
		scene.add(ambient);

		// // 2) Key (directional) light: main source of form & shadow
		const keyLight = new THREE.DirectionalLight(0xffffff, 0.6);
		keyLight.position.set(0, 100, 100); // tweak this to get the angle you like
		keyLight.castShadow = true; // if you want shadows
		keyLight.shadow.camera.near = 1;
		keyLight.shadow.camera.far = 50;
		keyLight.castShadow = true;
		keyLight.shadow.mapSize.set(1024, 1024);
		scene.add(keyLight);

		// 3) Back / rim light: subtle highlight on edges
		const backLight = new THREE.DirectionalLight(0xffffff, 0.4);
		backLight.position.set(-5, 5, -5);
		scene.add(backLight);

		let lastTime = 0;
		const targetFPS = assets.lowPower ? 30 : 60;
		const frameInterval = 1000 / targetFPS;

		const animate = (time: number) => {
			const delta = time - lastTime;
			if (delta > frameInterval) {
				controls.update();
				renderer.render(scene, camera);
				lastTime = time;
			}
			animationIdRef.current = requestAnimationFrame(animate);
		};
		animationIdRef.current = requestAnimationFrame(animate);

		const handleResize = () => {
			if (!mountRef.current) return;
			const width = mountRef.current.clientWidth;
			const height = mountRef.current.clientHeight;
			renderer.setSize(width, height);
			camera.aspect = width / height;
			camera.updateProjectionMatrix();
			frameContent(camera, controls, boundingSize, objectHeight);
		};
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);

			// Dispose scene children
			scene.traverse(obj => {
				if (obj instanceof THREE.Mesh) {
					obj.geometry?.dispose();
					if (Array.isArray(obj.material)) {
						obj.material.forEach(m => m.dispose());
					} else {
						obj.material?.dispose();
					}
				}
			});

			// Clean up renderer
			if (renderer) {
				const gl = renderer.getContext();
				const loseContext = gl.getExtension('WEBGL_lose_context');
				if (loseContext) loseContext.loseContext();

				renderer.dispose();
				if (renderer.domElement && mountRef.current) {
					mountRef.current.removeChild(renderer.domElement);
				}
			}
		};
	}, [mountRef, transparent]);

	const frameContent = (
		camera: THREE.PerspectiveCamera,
		controls: OrbitControls,
		baseSize: number,
		height: number
	) => {
		const heightFactor = height > 0 ? Math.max(1, height / baseSize) : 1;
		const aspectFactor = camera.aspect < 1 ? 1.2 : 1;
		const distance = baseSize * 1.5 * heightFactor * aspectFactor;
		const verticalOffset = height * 0.3;
		camera.position.set(0, distance * 0.8, distance);
		controls.target.set(0, verticalOffset, 0);
		controls.update();
		camera.updateProjectionMatrix();
	};

	useEffect(() => {
		if (!cameraRef.current || !controlsRef.current || !mountRef.current) return;
		const camera = cameraRef.current;
		const controls = controlsRef.current;
		const width = mountRef.current.clientWidth;
		const height = mountRef.current.clientHeight;
		camera.aspect = width / height;
		frameContent(camera, controls, boundingSize, objectHeight);
	}, [boundingSize, objectHeight, mountRef]);

	useEffect(() => {
		if (controlsRef.current) {
			controlsRef.current.autoRotate = autoRotate ?? false;
		}
	}, [autoRotate]);

	return {
		sceneRef,
		cameraRef,
		rendererRef,
		controlsRef,
		frameContent: (bound?: number, height?: number) => {
			if (cameraRef.current && controlsRef.current) {
				frameContent(
					cameraRef.current,
					controlsRef.current,
					bound ?? boundingSize,
					height ?? objectHeight
				);
			}
		},
	};
}
